import { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid2";
import Typography from "@mui/material/Typography";
import { useAppDispatch } from "hooks/useAppDispatch";
import { ILoginCredentials } from "types/auth";
import { checkLoggedUserThunk, logInThunk } from "reduxStore/auth/thunks";
import { authStatusSelector } from "reduxStore/auth/selectors";
import { useAppSelector } from "hooks/useAppSelector";
import { StyledFormicError } from "modals/ResetPasswordModal/styles";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import {
  LoginResponseType,
  LoginSteps,
  Messages,
  NotificationSeverity,
} from "helpers/constants";
import { showNotificationThunk } from "reduxStore/alert/thunks";
import { AuthContainer } from "components/Layout/AuthContainer";
import { Password } from "../../components/Password";
import {
  emailRequiresSso,
  getOktaIdpName,
  isOAuthDomainConfigured,
  isSsoRequired,
} from "config/amplifyAuth";
import { signInWithRedirect } from "aws-amplify/auth";
import { AdminRoutes } from "config/routes";

const MFA_STEPS = new Set<string>([
  LoginSteps.CONTINUE_SIGN_IN_WITH_MFA_SELECTION,
  LoginSteps.CONTINUE_SIGN_IN_WITH_TOTP_SETUP,
  LoginSteps.CONFIRM_SIGN_IN_WITH_SMS_CODE,
  LoginSteps.CONFIRM_SIGN_IN_WITH_TOTP_CODE,
  LoginSteps.CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE,
]);

export default function SignInSide() {
  const dispatch = useAppDispatch();
  const statuses = useAppSelector(authStatusSelector);
  const loginError = statuses["auth/logInThunk"].error;
  const loginSuccess = statuses["auth/logInThunk"].success;
  const [loading, setLoading] = useState(false);
  const [emptyFieldsError, setEmptyFieldsError] = useState(false);
  const action = statuses["auth/logInThunk"].action;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefilledEmail = searchParams.get("email")?.trim() || "";
  const ssoOnly = isSsoRequired();
  const showOkta = isOAuthDomainConfigured();

  useEffect(() => {
    dispatch(checkLoggedUserThunk());
  }, [dispatch]);

  if (loginSuccess && action) {
    switch (action) {
      case LoginSteps.CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED:
        return <Navigate to="/verify" />;
      case LoginSteps.RESET_PASSWORD:
        dispatch(
          showNotificationThunk({
            message: Messages.errorNeedVerification,
            severity: NotificationSeverity.error,
          }),
        );
        break;
      default:
        if (MFA_STEPS.has(action)) {
          return <Navigate to={AdminRoutes.Mfa} />;
        }
        break;
    }
  }

  const handleSignInWithOkta = async () => {
    if (!showOkta) return;
    setLoading(true);
    try {
      const provider = getOktaIdpName();
      await signInWithRedirect({ provider: { custom: provider } });
    } catch (err) {
      setLoading(false);
      console.error("[signInWithRedirect] error:", err);
      dispatch(
        showNotificationThunk({
          message: "Sign in with Okta could not be started.",
          severity: NotificationSeverity.error,
        }),
      );
    }
  };

  const handleLogin = async (values: ILoginCredentials) => {
    const username = values.username?.toString().trim() || "";
    if (!ssoOnly && (username === "" || values.password === "")) {
      setEmptyFieldsError(true);
      return;
    }
    if (ssoOnly) {
      await handleSignInWithOkta();
      return;
    }
    if (emailRequiresSso(username)) {
      setEmptyFieldsError(false);
      await handleSignInWithOkta();
      return;
    }
    setEmptyFieldsError(false);
    setLoading(true);
    const { payload }: any = await dispatch(logInThunk(values));
    setLoading(false);
    if (payload?.type === LoginResponseType.SUCCESS) {
      window.location.reload();
    }
  };

  const handleSubmit = async (event: {
    preventDefault: () => void;
    currentTarget: HTMLFormElement | undefined;
  }) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const values = {
      username: data.get("email")?.toString()?.trim() || "",
      password: data.get("password"),
    };
    await handleLogin(values);
  };

  return (
    <AuthContainer loading={loading} loadingText="Signing In...">
      <Box sx={{ width: "100%", maxWidth: 420 }}>
        <Typography component="h1" variant="h6">
          Sign In
        </Typography>
        {ssoOnly && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Your institution requires single sign-on. Use the Okta button below.
          </Typography>
        )}
        <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 1 }}>
          {!ssoOnly && (
            <>
              <TextField
                variant="standard"
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                defaultValue={prefilledEmail}
              />
              <Password name="password" label="Password" required={true} />
              <Button
                disabled={loading}
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                sx={{ mt: 3, mb: 0 }}
              >
                Sign In
              </Button>
            </>
          )}

          <Grid container>
            {loginError && (
              <StyledFormicError>
                {Messages.errorInvalidCredentials}
              </StyledFormicError>
            )}
            {emptyFieldsError && (
              <StyledFormicError>{Messages.errorEmptyFields}</StyledFormicError>
            )}
          </Grid>

          {!ssoOnly && (
            <Button
              disabled={loading}
              onClick={() => navigate("/login-code")}
              fullWidth
              variant="outlined"
              color="primary"
              sx={{ mt: 3, mb: 2 }}
            >
              Send A Login Code
            </Button>
          )}

          {showOkta && (
            <Button
              disabled={loading}
              onClick={() => {
                void handleSignInWithOkta();
              }}
              fullWidth
              variant={ssoOnly ? "contained" : "outlined"}
              color="primary"
              sx={{ mt: ssoOnly ? 3 : 1, mb: 2 }}
            >
              Sign in with Okta
            </Button>
          )}

          {!ssoOnly && (
            <>
              <Button
                disabled={loading}
                fullWidth
                variant="outlined"
                color="success"
                sx={{ mt: 1, mb: 1 }}
                onClick={() => navigate("/find-account")}
              >
                Find My Account
              </Button>
              <Button
                disabled={loading}
                fullWidth
                variant="outlined"
                color="error"
                sx={{ mt: 1, mb: 2 }}
                onClick={() => navigate("/reset-request")}
              >
                Reset Password
              </Button>
            </>
          )}
        </Box>
      </Box>
    </AuthContainer>
  );
}
